import { Fragment, createContext, createElement, useContext, type ComponentType, type ReactNode } from 'react';
import { composeWithUi, type ControlElement, type JsonFormsRendererRegistryEntry, type OwnPropsOfRenderer, type UISchemaElement } from '@jsonforms/core';

/**
 * A mark a form puts on its controls — the tint of a value a copy filled (entity-templates spec §15.7), of a value
 * not saved yet — by decorating every renderer once. No renderer changes, so every current and future renderer is
 * marked. A mark receives the control's data path and its rendered control; anything that is not a control renders
 * untouched.
 *
 * A renderer that hands its own control on to another renderer for the same path (ComputedDefaultControl,
 * EntityBackedRowConfigRenderer) wraps that hand-off in {@link DelegatedControlContext}: the control is then marked
 * once, by the outer renderer, never again by the one it delegates to. Such a renderer leaves itself out of the set
 * it delegates to by its TESTER, which the decoration keeps — the renderer component is the decoration's wrapper.
 */
export type ControlMark = ComponentType<{ dataPath: string; children: ReactNode }>;

type RendererComponent = ComponentType<OwnPropsOfRenderer>;

/** The data path a delegating renderer has already rendered, and so marked; null outside a hand-off. */
export const DelegatedControlContext = createContext<string | null>(null);

const decorated = new WeakMap<ControlMark, WeakMap<RendererComponent, RendererComponent>>();

/** Every renderer, decorated once per mark: a control is rendered inside the mark. */
export function withControlMark(renderers: readonly JsonFormsRendererRegistryEntry[], mark: ControlMark): JsonFormsRendererRegistryEntry[] {
  return renderers.map((entry) => ({ tester: entry.tester, renderer: markedRenderer(entry.renderer as RendererComponent, mark) }));
}

function markedRenderer(renderer: RendererComponent, mark: ControlMark): RendererComponent {
  let cache = decorated.get(mark);
  if (!cache) {
    cache = new WeakMap();
    decorated.set(mark, cache);
  }
  const existing = cache.get(renderer);
  if (existing) return existing;
  const wrapped = (props: OwnPropsOfRenderer) =>
    createElement(MarkBoundary, { mark, uischema: props.uischema, path: props.path, children: createElement(renderer, props) });
  cache.set(renderer, wrapped);
  return wrapped;
}

function MarkBoundary({ mark, uischema, path, children }: { mark: ControlMark; uischema?: UISchemaElement; path?: string; children: ReactNode }) {
  const delegated = useContext(DelegatedControlContext);
  if (uischema?.type !== 'Control') return createElement(Fragment, null, children);
  const dataPath = composeWithUi(uischema as ControlElement, path ?? '');
  if (dataPath === delegated) return createElement(Fragment, null, children);
  return createElement(mark, { dataPath, children });
}

/** The renderers to hand a control on to, without the delegating renderer itself (by its tester, see above). */
export function delegateTo(
  renderers: readonly JsonFormsRendererRegistryEntry[],
  own: JsonFormsRendererRegistryEntry['tester'],
): JsonFormsRendererRegistryEntry[] {
  return renderers.filter((entry) => entry.tester !== own);
}
